// filepath: apps/web/src/renderers/form/fields/reference/useReferencePicker.ts
//
// FR-UI-FORM-1 AC-2 — TanStack Query hook backing ReferencePicker.
//
// Issues GET /api/entity-records/{entityType} against the FR-QRY-1
// query API with:
//   - page_size=20 (picker cap)
//   - filter=[{"field":<displayField>,"op":"contains","value":<search>}]
//     when the user has typed a search term; omitted for an empty
//     search (returns the first page sorted by displayField).
//   - sort=<displayField>:asc (server-side canonical sort).
//
// Response envelope:
//
//   { records: [...EntityRecord], next_cursor: string, page_size: number }
//
// The EntityRecord shape serializes from Go with default JSON field
// names — IDs come back as `ID` and field values live under `Fields`.
// The hook normalises each record to
// `{id: string, [displayField]: string}` so the picker is decoupled
// from the wire shape.
//
// SECURITY & INTEGRITY INVARIANTS:
//   - INV-1: the cache key always includes `tenantSlug` as the
//     second segment so a TanStack Query cache for tenant A can never
//     serve options to tenant B. Mirrors the pattern at
//     `apps/web/src/definitions/useDefinition.ts` and
//     `apps/web/src/renderers/form/useFieldEvaluations.ts`.
//   - INV-2: an HTTP 200 with `records: []` is treated as a successful
//     empty result set, not an error. This is the FR-QRY-3 AC-1 "no
//     CanRead → 200 with empty records" path — the renderer must
//     NEVER leak existence of the target entity type to a role that
//     cannot read it (no permission-denied banner on 200-empty).
//   - INV-5: no JWT, token, or secret is placed in the URL or body.
//     `credentials: 'include'` attaches the same-origin session cookie;
//     the body carries no auth material.
//   - INV-9: row labels flow through React text nodes; no HTML.
//   - The hook does NOT issue a per-row GET to "fill in" stripped
//     fields. The list endpoint is the only server contact; whatever
//     the server returned is what the picker renders.
//
// BEHAVIOUR:
//   - `search` empty → fetch first page of options (no filter) sorted
//     by displayField.
//   - `search` non-empty → debounce 200 ms, then fetch with filter.
//   - 4xx/5xx throws so the component renders an error state.
//   - retry: false (matches the platform-wide renderer discipline).
//   - staleTime: 30_000 ms (entity data; per ARCHITECTURE_WEB §4).

import { useEffect, useMemo, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiError } from "@/definitions/types";
import { useTenant } from "@/tenant/TenantProvider";
import { apiFetch } from "@/api";

/**
 * One row of picker results, normalised to the shape the picker
 * consumes. `id` is the record id (string); `[displayField]` is the
 * configured display field on the target entity.
 */
export interface ReferencePickerRow {
  id: string;
  /** Display label, taken from the target entity's display field. */
  label: string;
}

/**
 * Result envelope returned by the hook.
 *
 * `nextCursor` is the base64url-encoded keyset cursor from the
 * server's `next_cursor` field. The picker consumes it for pagination
 * (cursor is appended to the cache key so a new page lands in a new
 * cache entry).
 */
export interface ReferencePickerResult {
  records: ReferencePickerRow[];
  nextCursor: string | null;
}

/** Wire shape of one record returned by GET /api/entity-records/{type}. */
interface EntityRecordWire {
  ID: string;
  TenantID?: string;
  RowVersion?: number;
  CreatedAt?: string;
  UpdatedAt?: string;
  Fields?: Record<string, unknown>;
}

interface ListResponseWire {
  records: EntityRecordWire[];
  next_cursor: string;
  page_size: number;
}

export interface UseReferencePickerOptions {
  /** Target entity type id (e.g. "customer"). Required. */
  entityType: string;
  /** Field on the target entity to display in the picker (e.g. "name"). Required. */
  displayField: string;
  /**
   * Current search input. Empty string → fetch first page of options
   * sorted by displayField. Non-empty → debounce 200 ms, then fetch
   * with `filter[<displayField>][contains]=<search>`.
   */
  search: string;
  /**
   * Cursor for the next page. When set, the hook appends it to the
   * cache key so a new page lands in its own cache entry. The picker
   * manages this as state.
   */
  cursor?: string | null;
  /** API base URL override. Defaults to `/api`. */
  apiBase?: string;
  /** Debounce window in ms for the search term. Defaults to 200 ms. */
  debounceMs?: number;
  /**
   * When true, the query is disabled (no fetch). Used when the field
   * is missing the required metadata (graceful degradation per
   * FR-UI-FORM-1 additive FieldMetadata change).
   */
  enabled?: boolean;
}

export interface UseReferencePickerResult {
  /** Normalised result envelope. */
  data: ReferencePickerResult | undefined;
  /** True while the initial (or cursor-advanced) fetch is in flight. */
  isLoading: boolean;
  /** True while any fetch is in flight (including background refetch). */
  isFetching: boolean;
  /** Last error, classified as an `ApiError` on non-2xx responses. */
  error: ApiError | null;
}

/**
 * useReferencePicker — TanStack Query hook backing the picker.
 *
 * See file header for invariants and security boundaries.
 */
export function useReferencePicker(
  opts: UseReferencePickerOptions,
): UseReferencePickerResult {
  const {
    entityType,
    displayField,
    search,
    cursor = null,
    apiBase = "/api",
    debounceMs = 200,
    enabled = true,
  } = opts;

  const { tenantSlug } = useTenant();

  // Debounce the search term so a typing burst collapses to one fetch.
  const debouncedSearch = useDebouncedValue(search, debounceMs);

  // The cache key composes (tenantSlug, entityType, search, cursor) so:
  //   - cross-tenant cache hits are impossible (INV-1)
  //   - the empty-search first page is a distinct entry from a typed
  //     "ada" search (the picker falls back to it when the user clears
  //     the input).
  //   - the cursor advances the cache to a new entry on "load more"
  //     pagination.
  const searchKey = debouncedSearch.trim();
  const queryKey = useMemo(
    () => [
      "reference-options",
      tenantSlug,
      entityType,
      displayField,
      searchKey,
      cursor ?? "",
    ],
    [tenantSlug, entityType, displayField, searchKey, cursor],
  );

  const query: UseQueryResult<ReferencePickerResult, ApiError> = useQuery<
    ReferencePickerResult,
    ApiError
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set("page_size", "20");
      params.set("sort", `${displayField}:asc`);
      if (searchKey.length > 0) {
        // The backend parses `filter` as a URL-encoded JSON array of
        // {field, op, value} objects. See internal/query/handlers.go:96.
        const filterPayload = JSON.stringify([
          { field: displayField, op: "contains", value: searchKey },
        ]);
        params.set("filter", filterPayload);
      }
      if (cursor !== null && cursor !== "") {
        params.set("cursor", cursor);
      }
      const url = `${apiBase}/entity-records/${encodeURIComponent(
        entityType,
      )}?${params.toString()}`;

      let response: Response;
      try {
        // ISS-377 round 3 R2-B2.5: migrated to apiFetch. The
        // AbortSignal is forwarded via `init.signal` so TanStack
        // Query's unmount-cancellation reaches the underlying
        // fetch.
        response = await apiFetch(url, {
          method: "GET",
          init: {
            headers: { Accept: "application/json" },
            signal,
          },
        });
      } catch (networkErr) {
        throw new ApiError(
          networkErr instanceof Error ? networkErr.message : "network error",
          0,
          "network_error",
        );
      }

      if (!response.ok) {
        const body = await safeReadJson(response);
        const code =
          (body as { code?: string } | null)?.code ?? `http_${response.status}`;
        throw new ApiError(
          (body as { error?: string } | null)?.error ?? response.statusText,
          response.status,
          code,
        );
      }

      const data = (await response.json()) as ListResponseWire;
      return normaliseList(data, displayField);
    },
    enabled: enabled && entityType.length > 0 && displayField.length > 0,
    staleTime: 30_000,
    gcTime: 5 * 60 * 1000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
  };
}

/**
 * Normalise the wire shape into the picker's local envelope.
 *
 * The Go server returns records with Go's default JSON field names
 * (`ID`, `Fields[name]`, etc.). The picker is decoupled from this
 * shape — it consumes `{id, label}` only.
 */
function normaliseList(
  wire: ListResponseWire,
  displayField: string,
): ReferencePickerResult {
  const records: ReferencePickerRow[] = Array.isArray(wire.records)
    ? wire.records.map((r) => ({
        id: r.ID,
        label: extractLabel(r, displayField),
      }))
    : [];
  const nextCursor =
    typeof wire.next_cursor === "string" && wire.next_cursor.length > 0
      ? wire.next_cursor
      : null;
  return { records, nextCursor };
}

/**
 * Extract the display label from a record. The wire shape stores
 * field values under `Fields[<displayField>]`. Falls back to the id
 * when the display field is missing or empty (server-stripping path
 * per INV-2 — the field may have been stripped because the caller
 * lacks read permission on it).
 */
function extractLabel(record: EntityRecordWire, displayField: string): string {
  const value = record.Fields?.[displayField];
  if (value === null || value === undefined) return record.ID;
  if (typeof value === "string") return value;
  // Non-string values (number, boolean, nested object) are coerced to
  // a string for display. The server-stripping case means the caller
  // cannot read this field, so the id is the safe fallback.
  try {
    return JSON.stringify(value);
  } catch {
    return record.ID;
  }
}

async function safeReadJson(response: Response): Promise<unknown> {
  try {
    return (await response.json()) as unknown;
  } catch {
    return null;
  }
}

/**
 * useDebouncedValue — returns `value` after it has been stable for
 * `delay` ms. Used to collapse typing bursts into a single fetch.
 * Mirrors the same utility used by useFieldEvaluations.
 */
function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState<T>(value);
  // Dependency on a stable string form so callers passing a freshly-
  // allocated primitive with the same content do not reset the timer.
  const serialized = useMemo(() => stableStringify(value), [value]);

  useEffect(() => {
    if (delay <= 0) {
      setDebounced(value);
      return;
    }
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized, delay]);

  return debounced;
}

function stableStringify(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
}
